/**
 * META1-07 (PR #352): renderizado visual de resultados de tools.
 *
 * Cada tool tiene su mini-template visual. Sirve como receipt visual de la
 * acción ejecutada por el asistente.
 */
import React from 'react';
import { ToolCallResult } from '../api/agent-assistant-api';

export interface ToolResultCardProps {
  result: ToolCallResult;
}

export const ToolResultCard: React.FC<ToolResultCardProps> = ({ result }) => {
  if (result.requiresConfirmation) {
    return null; // ConfirmActionDialog handles this UI separately
  }

  if (!result.success) {
    return (
      <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
        <div className="font-semibold">Acción no ejecutada: {result.toolName}</div>
        <div>{result.error}</div>
      </div>
    );
  }

  const r = result.result ?? {};

  switch (result.toolName) {
    case 'guardar_memoria':
      return (
        <div className="mt-2 rounded border border-blue-200 bg-blue-50 p-2 text-xs">
          <div className="font-semibold text-blue-800">🧠 Memoria guardada</div>
          <div className="text-blue-700">{r.content}</div>
          <div className="mt-1 text-blue-500">Categoría: {r.category}</div>
        </div>
      );
    case 'agendar_visita':
      return (
        <div className="mt-2 rounded border border-green-200 bg-green-50 p-2 text-xs">
          <div className="font-semibold text-green-800">📅 Visita agendada</div>
          <div className="text-green-700">
            Lead: {r.leadId} · Propiedad: {r.propertyId}
          </div>
          <div className="text-green-700">Para: {new Date(r.scheduledFor).toLocaleString()}</div>
          {r.stub && (
            <div className="mt-1 italic text-green-500">(MVP — wire pending)</div>
          )}
        </div>
      );
    case 'crear_lead':
      return (
        <div className="mt-2 rounded border border-purple-200 bg-purple-50 p-2 text-xs">
          <div className="font-semibold text-purple-800">👤 Lead creado</div>
          <div className="text-purple-700">{r.fullName}</div>
          <div className="text-purple-500">ID: {r.leadId}</div>
        </div>
      );
    case 'reasignar_lead':
      return (
        <div className="mt-2 rounded border border-yellow-200 bg-yellow-50 p-2 text-xs">
          <div className="font-semibold text-yellow-800">🔄 Lead reasignado</div>
          <div className="text-yellow-700">
            Lead {r.leadId} → Agente {r.newAgentId}
          </div>
          {r.reason && <div className="text-yellow-600">Motivo: {r.reason}</div>}
        </div>
      );
    case 'cambiar_estado_propiedad':
      return (
        <div className="mt-2 rounded border border-orange-200 bg-orange-50 p-2 text-xs">
          <div className="font-semibold text-orange-800">🏠 Estado actualizado</div>
          <div className="text-orange-700">
            Propiedad {r.propertyId} → {r.newStatus}
          </div>
        </div>
      );
    case 'consultar_comisiones':
      return (
        <div className="mt-2 rounded border border-emerald-200 bg-emerald-50 p-2 text-xs">
          <div className="font-semibold text-emerald-800">💰 Comisiones</div>
          <div className="text-emerald-700">Periodo: {r.period}</div>
          <div className="text-emerald-700">
            Pendiente: ${r.totalPending} · Pagado: ${r.totalPaid}
          </div>
        </div>
      );
    case 'generar_descripcion_propiedad':
      return (
        <div className="mt-2 rounded border border-indigo-200 bg-indigo-50 p-2 text-xs">
          <div className="font-semibold text-indigo-800">✍️ Descripción generada</div>
          <div className="mt-1 text-indigo-700 italic">{r.description}</div>
        </div>
      );
    case 'enviar_link_cliente':
      return (
        <div className="mt-2 rounded border border-cyan-200 bg-cyan-50 p-2 text-xs">
          <div className="font-semibold text-cyan-800">📤 Link enviado</div>
          <div className="text-cyan-700">
            Lead {r.leadId} via {r.channel}
          </div>
        </div>
      );
    default:
      return (
        <div className="mt-2 rounded border border-gray-200 bg-gray-50 p-2 text-xs">
          <div className="font-semibold text-gray-800">{result.toolName}</div>
          <pre className="text-gray-600">{JSON.stringify(r, null, 2)}</pre>
        </div>
      );
  }
};
